import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, X } from "lucide-react";
import { MaskLines, Reveal, ClipImage, EASE } from "./Reveal";
import { GrainButton } from "./GrainButton";
import { openInquiry } from "../lib/scroll";
import { PROJECTS } from "../data/content";

const FEATURED = PROJECTS.slice(0, 3);

const LAYOUTS = [
  { img: "lg:col-span-8", txt: "lg:col-span-4", aspect: "aspect-[16/10]", overlap: "lg:-ml-24 lg:pb-10" },
  { img: "lg:col-span-7 lg:col-start-6", txt: "lg:col-span-4 lg:col-start-1 lg:row-start-1", aspect: "aspect-[4/3]", overlap: "lg:-mr-24 lg:pb-12 lg:text-right lg:items-end" },
  { img: "lg:col-span-8 lg:col-start-3", txt: "lg:col-span-4 lg:col-start-1 lg:row-start-1", aspect: "aspect-[21/11]", overlap: "lg:pt-10" },
];

const ProjectRow = ({ project, index, onSelect }) => {
  const l = LAYOUTS[index % LAYOUTS.length];
  const slug = project.title.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <Reveal y={50}>
      <div
        data-testid={`project-card-${slug}`}
        onClick={() => onSelect(index)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && onSelect(index)}
        className="group grid lg:grid-cols-12 gap-6 items-end cursor-pointer"
      >
        <div className={`${l.img} relative`}>
          <ClipImage
            src={project.img}
            alt={project.title}
            className={`${l.aspect} w-full`}
            parallax={6}
            rounded="rounded-[1.75rem]"
            imgClassName="transition-transform duration-[1300ms] ease-out group-hover:scale-[1.2]"
          />
          <span className="absolute top-5 left-5 font-mono text-[11px] tracking-[0.22em] text-cream/90 bg-charcoal/40 backdrop-blur px-3 py-1.5 rounded-full">
            {String(index + 1).padStart(2, "0")}
          </span>
        </div>
        <div className={`${l.txt} relative z-10 flex flex-col ${l.overlap}`}>
          <p className="font-mono text-[11px] tracking-[0.22em] text-ember mb-3">{project.location.toUpperCase()}</p>
          <h3 className="font-display font-medium tracking-[-0.02em] text-2xl sm:text-4xl lg:text-[3.2rem] leading-[1.04] text-charcoal transition-transform duration-500 group-hover:translate-x-2">
            {project.title}
          </h3>
          <span className="mt-6 inline-flex w-12 h-12 rounded-full border border-charcoal/20 items-center justify-center text-charcoal transition-all duration-500 group-hover:bg-flame group-hover:border-flame group-hover:text-white group-hover:rotate-45">
            <ArrowUpRight className="w-5 h-5" />
          </span>
        </div>
      </div>
    </Reveal>
  );
};

export const FeaturedWork = () => {
  const [selected, setSelected] = useState(null);

  return (
    <section id="work" data-testid="featured-work-section" className="px-6 sm:px-10 lg:px-16 py-16 sm:py-36 max-w-[1700px] mx-auto">
      <div className="grid lg:grid-cols-12 gap-8 items-end mb-16 lg:mb-28">
        <div className="lg:col-span-8">
          <p className="font-mono text-[11px] sm:text-xs tracking-[0.28em] text-ember mb-5">OUR WORK</p>
          <MaskLines
            as="h2"
            lines={[<>Built for properties <span className="font-serif italic font-normal">that matter.</span></>]}
            className="font-display font-medium tracking-[-0.03em] leading-[1.05] text-3xl sm:text-5xl lg:text-[4.2rem] text-charcoal"
          />
        </div>
        <Reveal className="lg:col-span-4 lg:pb-3" delay={0.15}>
          <p className="text-sm sm:text-base text-charcoal/60 leading-relaxed">
            From commercial developments and retail centers to parks, streetscapes, institutions and private residences, every Nagel project starts with the same goal:
          </p>
          <p className="mt-3 font-display font-semibold tracking-[-0.01em] text-lg sm:text-xl text-charcoal">
            Build it right. Make it work. Make it last.
          </p>
        </Reveal>
      </div>

      <div className="space-y-20 lg:space-y-36">
        {FEATURED.map((p, i) => (
          <ProjectRow key={p.title} project={p} index={i} onSelect={setSelected} />
        ))}
      </div>

      <Reveal className="mt-20 lg:mt-28 flex flex-wrap items-center justify-center gap-4" y={20}>
        <GrainButton testid="work-send-inquiry-button" onClick={() => openInquiry()}>
          SEND INQUIRY
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </GrainButton>
        <button
          data-testid="view-all-projects-button"
          onClick={() => openInquiry()}
          className="group inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] text-charcoal border border-charcoal/20 rounded-full px-8 py-4 hover:bg-charcoal hover:text-cream transition-colors duration-300"
        >
          VIEW ALL PROJECTS
          <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" />
        </button>
      </Reveal>

      <AnimatePresence>
        {selected !== null && (
          <motion.div
            data-testid="project-overlay"
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-charcoal/60 backdrop-blur-md" onClick={() => setSelected(null)} />
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.5, ease: EASE }}
              className="relative w-full max-w-4xl bg-cream rounded-[2rem] overflow-hidden shadow-2xl"
            >
              <div className="relative aspect-[16/9] overflow-hidden">
                <img src={FEATURED[selected].img} alt={FEATURED[selected].title} className="absolute inset-0 w-full h-full object-cover" />
                <button
                  data-testid="project-overlay-close"
                  onClick={() => setSelected(null)}
                  className="absolute top-4 right-4 w-10 h-10 rounded-full bg-cream/90 backdrop-blur flex items-center justify-center text-charcoal hover:bg-flame hover:text-white transition-colors duration-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-7 sm:p-10 flex flex-wrap items-end justify-between gap-6">
                <div>
                  <p className="font-mono text-[11px] tracking-[0.22em] text-ember mb-2">
                    {String(selected + 1).padStart(2, "0")} — {FEATURED[selected].location.toUpperCase()}
                  </p>
                  <h3 className="font-display font-medium tracking-[-0.02em] text-2xl sm:text-3xl text-charcoal">
                    {FEATURED[selected].title}
                  </h3>
                </div>
                <button
                  data-testid="project-overlay-inquiry-button"
                  onClick={() => { setSelected(null); openInquiry(); }}
                  className="group inline-flex items-center gap-2 rounded-full bg-charcoal text-cream px-6 py-3 text-sm font-semibold hover:bg-flame transition-colors duration-300"
                >
                  START A PROJECT LIKE THIS
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
